import { RouteProp, useRoute } from '@react-navigation/core';
import {
  BaseText,
  BottomButtonsLayout,
  Button,
  Headline1,
  Meta,
  theme,
} from 'aimo-ui';
import MenuItem from 'components/common/MenuItem';
import AttachmentCardRow from 'components/longTermParking/AttachmentCardRow';
import { useWaitingListAttachmentFlow } from 'components/attachments';
import { useAttachmentStore } from 'stores/attachmentStore';
import QuickActionDirection from 'components/common/QuickActionDirection';
import WaitingNumber from 'components/longTermParking/waitingList/WaitingNumber';
import Sheet from 'components/sheets/Sheet';
import AccountContext from 'context/AccountContext';
import React, { useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import SalesOrderPermiPriceInfo from 'screens/permit/salesOrder/SalesOrderPermitPriceInfo';
import styled from 'styled-components/native';
import {
  ContainerWithGap,
  ScreenContainer,
  StyledWrapper,
} from 'styles/ContainerStyles';
import { LongTermStackParamList } from 'types/navigation';
import { isWeb } from 'utils/deviceUtils';
import { makePoint } from 'utils/mapUtils';
import { getLocationIconType, locationIcon } from 'utils/parkUtils';
import { getPermitName } from 'utils/permitUtils';
import { mapAttachmentsToFiles } from 'utils/attachmentUtils';
import { useUiStore } from 'stores/uiStore';

const GreyWrapper = styled.View`
  background-color: ${isWeb ? theme.colors.white : theme.colors.gray100};
`;

const Container = styled(ContainerWithGap)`
  padding: 0 20px;
`;

const WaitingListPermitDetailsScreen = () => {
  const { t } = useTranslation();
  const { params } =
    useRoute<RouteProp<LongTermStackParamList, 'waitingListPermitDetails'>>();
  const {
    customer: { permits },
  } = useContext(AccountContext);

  const permit = permits?.find((p) => p.id === params?.id);
  const waitingListAttachmentFlow = useWaitingListAttachmentFlow();

  // Mount-scoped setup: bind the attachment context to this permit and put
  // the screen in read-only mode. Keyed on `permit?.id` so the user's
  // `selectedAttachment` survives Apollo refetches that produce a new
  // `permit` object reference with the same id.
  useEffect(() => {
    if (!permit?.id) return;
    const {
      setCurrentAttachmentContext,
      setAttachmentReadOnly,
      setSelectedAttachment,
    } = useAttachmentStore.getState();
    setCurrentAttachmentContext(permit.id);
    setAttachmentReadOnly(true);
    return () => {
      setAttachmentReadOnly(false);
      setCurrentAttachmentContext(null);
      setSelectedAttachment(null);
    };
  }, [permit?.id]);

  // Flow registration is independent of the permit identity.
  useEffect(() => {
    const { setAttachmentFlow } = useAttachmentStore.getState();
    setAttachmentFlow(waitingListAttachmentFlow);
    return () => {
      setAttachmentFlow(null);
    };
  }, [waitingListAttachmentFlow]);

  // Sync the permit's attachments into the global list. Safe to re-run on
  // Apollo refetches because it only replaces this permit's entries; the
  // user's `selectedAttachment` is owned by the mount-scoped effect.
  useEffect(() => {
    if (!permit) return;
    const permitId = permit.id;
    const source =
      permit.orderStatus === 'WAITING_FOR_CSR_APPROVAL' ? 'APPROVAL' : 'ITEM';
    const mappedAttachments = mapAttachmentsToFiles(
      permit.attachments,
      permitId,
      t('longTermParking.attachment.fallbackName'),
      source
    );
    const { attachments, setAttachments } = useAttachmentStore.getState();
    setAttachments([
      ...attachments.filter((a) => a.contextId !== permitId),
      ...mappedAttachments,
    ]);
  }, [permit, t]);

  // Drop this permit's entries from the global list when leaving the screen
  // or switching to a different permit. Separate effect so it doesn't
  // flicker during a same-permit refetch.
  useEffect(() => {
    const permitId = permit?.id;
    if (!permitId) return;
    return () => {
      const { attachments, setAttachments } = useAttachmentStore.getState();
      setAttachments(attachments.filter((a) => a.contextId !== permitId));
    };
  }, [permit?.id]);

  if (!permit) {
    return null;
  }

  const handleCancelWaitingList = () => {
    useUiStore.getState().setSheetModal('leaveWaitingListModal');
  };

  const { positionInWaitingList, countryCode, billingFrequency, orderStatus } =
    permit;
  const pmc = permit.orderLines?.find(Boolean)?.pmc;
  const { parkZone } = pmc ?? {};

  const markerCoordinate =
    parkZone?.address?.marker &&
    makePoint(parkZone?.address?.marker?.coordinates);

  const street = parkZone?.address?.street ?? t('parkingZone.noAddress');
  const city = parkZone?.address?.city ?? '';

  const markerType = getLocationIconType(
    parkZone?.locationType || 'OTHER',
    parkZone?.parkingMethods?.anpr
  );

  const getAddressAndReservationType = () => {
    const address = `${street}${city ? ', ' + city : ''}`;
    const reservationType =
      pmc?.reservationType === 'RESERVED'
        ? t('longTermParking.reserved')
        : t('longTermParking.unReserved');
    return `${address}\n${reservationType}`;
  };

  return (
    <>
      <GreyWrapper>
        <StyledWrapper center mb={5}>
          <Headline1>{getPermitName(permit)}</Headline1>
        </StyledWrapper>
        <StyledWrapper center>
          <Meta color={theme.colors.gray300}>
            {t('services.permitParking')}
          </Meta>
        </StyledWrapper>
      </GreyWrapper>
      <ScreenContainer mb={1} mt={10} background={theme.colors.transparent}>
        <Sheet hideHandle snappingPoints={['100%']}>
          <BottomButtonsLayout
            contentBottom={
              <Container>
                <Button
                  size={'l'}
                  buttonType="tertiary"
                  text={t('longTermParking.waitingList.leave')}
                  onPress={handleCancelWaitingList}
                />
              </Container>
            }>
            <Container>
              <StyledWrapper mt={35} mb={0}>
                <BaseText center testID="permit-details-header">
                  {getAddressAndReservationType()}
                </BaseText>
              </StyledWrapper>
              <SalesOrderPermiPriceInfo
                pmc={pmc}
                totalPrice={undefined}
                useAdditionalDiscount={false}
                additionalDiscount={undefined}
                promotion={undefined}
                country={countryCode}
                billingFrequency={billingFrequency}
                billingType={undefined}
                validFrom={new Date()}
                serviceFee={0}
              />
              <WaitingNumber
                orderStatus={orderStatus ?? 'WAITING_FOR_ACTIVATION'}
                waitingCount={positionInWaitingList}
              />
              <MenuItem
                iconName={locationIcon[markerType]}
                menuLabel={street}
                rightComponent={
                  <QuickActionDirection
                    showIconLabel={false}
                    markerCoordinate={markerCoordinate}
                    alignEnd
                    name={getPermitName(permit)}
                  />
                }
              />
              <MenuItem
                iconName="Parking_Spot-Outlined"
                menuLabel={t('longTermParking.parkingSpace')}
                rightComponent={
                  <BaseText color={theme.colors.gray300}>
                    {t('longTermParking.any')}
                  </BaseText>
                }
              />
              {!!permit.attachments?.length && <AttachmentCardRow noBorder />}
            </Container>
          </BottomButtonsLayout>
        </Sheet>
      </ScreenContainer>
    </>
  );
};

export default WaitingListPermitDetailsScreen;
